import React, { useEffect, useState } from "react";
import { updateResearchDialogue } from "../../../Services/adminResearchLibrary";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Textarea } from "../../../ui/textarea";
import { buildOverridePatch, errorMessage } from "./researchLibraryFormat";

/**
 * Edit the member-facing title and description. Saved as admin overrides,
 * which survive every re-import; "Use release text" clears an override.
 */
const EditDialogueDialog = ({ open, onOpenChange, dialogue, onSaved }) => {
  const overrides = dialogue?.admin_overrides || {};
  const initialTitle = overrides.member_title ?? dialogue?.card_title ?? "";
  const initialDescription = overrides.description ?? dialogue?.description ?? "";
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [clearTitle, setClearTitle] = useState(false);
  const [clearDescription, setClearDescription] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTitle(initialTitle);
    setDescription(initialDescription);
    setClearTitle(false);
    setClearDescription(false);
    setError(null);
  }, [open, initialTitle, initialDescription]);

  const patch = buildOverridePatch({
    initialTitle,
    title,
    clearTitle,
    initialDescription,
    description,
    clearDescription,
  });
  const dirty = Object.keys(patch).length > 0;

  const handleSave = async () => {
    if (!dirty) return;
    setBusy(true);
    setError(null);
    try {
      const data = await updateResearchDialogue(dialogue.research_dialogue_id, patch);
      onSaved?.(data?.dialogue);
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err, "Failed to save the override"));
    } finally {
      setBusy(false);
    }
  };

  const handleOpenChange = (next) => {
    if (busy) return;
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit {dialogue?.research_dialogue_id}</DialogTitle>
          <DialogDescription>
            Overrides are kept across every future release import.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="rd-title">Member title</Label>
              {overrides.member_title != null && (
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={clearTitle}
                    onChange={(e) => setClearTitle(e.target.checked)}
                  />
                  Use release text
                </label>
              )}
            </div>
            <Input
              id="rd-title"
              value={title}
              disabled={clearTitle}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="rd-description">Description</Label>
              {overrides.description != null && (
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={clearDescription}
                    onChange={(e) => setClearDescription(e.target.checked)}
                  />
                  Use release text
                </label>
              )}
            </div>
            <Textarea
              id="rd-description"
              rows={5}
              value={description}
              disabled={clearDescription}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={busy || !dirty}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditDialogueDialog;
